import {
  Box,
  Card,
  CardBody,
  Flex,
  Button,
  useDisclosure,
  Modal,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Text,
  Badge,
  VStack,
  HStack,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useToast,
} from "@chakra-ui/react";
import { useRef } from "react";
import { InfoDisplayValue } from "components/DataDisplay/InfoDisplayValue";
import { useSSOProviderQuery, useDeleteSSOProvider } from "./hooks";
import SSOSettingsForm from "./SSOSettingsForm";
import { testSSOConnection, formatProviderName } from "./helpers";

export default function SSOSettings() {
  const { data: ssoProvider, isLoading } = useSSOProviderQuery();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const deleteMutation = useDeleteSSOProvider();
  const toast = useToast();

  const handleTestSSO = async () => {
    if (!ssoProvider) return;

    try {
      await testSSOConnection(ssoProvider);
      toast({
        title: "SSO Test Initiated",
        description: "A new window has opened to test your SSO configuration.",
        status: "info",
        duration: 5000,
      });
    } catch {
      toast({
        title: "Error Testing SSO",
        description: "There was an error initiating the SSO test.",
        status: "error",
        duration: 5000,
      });
    }
  };

  const handleDelete = async () => {
    await deleteMutation.mutateAsync();
    onDeleteClose();
  };

  if (isLoading) {
    return <Skeleton height="300px" />;
  }

  if (!ssoProvider) {
    // No SSO configured - show empty state
    return (
      <Box>
        <Card minWidth="900px" width="calc(100% - 200px)" mt="4">
          <CardBody>
            <VStack spacing={4} py={8}>
              <Text fontSize="lg" color="gray.600">
                No SSO provider configured
              </Text>
              <Text fontSize="sm" color="gray.500" textAlign="center" maxW="500px">
                Configure Single Sign-On (SSO) to allow your users to authenticate using your identity provider
              </Text>
              <Button colorScheme="blue" onClick={onOpen}>
                Configure SSO
              </Button>
            </VStack>
          </CardBody>
        </Card>

        {/* Configuration Modal */}
        <Modal size="5xl" isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Configure SSO Provider</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <SSOSettingsForm onClose={onClose} />
            </ModalBody>
          </ModalContent>
        </Modal>
      </Box>
    );
  }

  // SSO is configured - show details
  return (
    <Box>
      <Card minWidth="900px" width="calc(100% - 200px)" mt="4">
        <CardBody>
          <Flex justifyContent="space-between" w="100%" alignItems="center" p={5}>
            <Box opacity="70%" fontSize="2xl">
              SSO Configuration
            </Box>
            <HStack>
              <Button onClick={handleTestSSO} variant="outline">
                Test SSO
              </Button>
              <Button onClick={onOpen}>Edit</Button>
              <Button onClick={onDeleteOpen} colorScheme="red" variant="outline">
                Delete
              </Button>
            </HStack>
          </Flex>

          <Flex direction="column" px={10} pb={5}>
            <VStack align="stretch" spacing={4}>
              {/* Provider Info */}
              <HStack>
                <InfoDisplayValue value={formatProviderName(ssoProvider.provider)} label="Provider" />
                <Badge colorScheme="blue" ml={2}>
                  {ssoProvider.protocol.toUpperCase()}
                </Badge>
              </HStack>

              {/* SAML Details */}
              {ssoProvider.protocol === "saml" && (
                <>
                  <InfoDisplayValue value={ssoProvider.ssoUrl} label="SSO URL" />
                  <InfoDisplayValue value={ssoProvider.issuer} label="Issuer (Entity ID)" />
                  {ssoProvider.metadataUrl && (
                    <InfoDisplayValue value={ssoProvider.metadataUrl} label="Metadata URL" />
                  )}
                  <InfoDisplayValue
                    value={ssoProvider.certificate ? "Configured" : "Not configured"}
                    label="Certificate"
                  />
                </>
              )}

              {/* OIDC Details */}
              {ssoProvider.protocol === "oidc" && (
                <>
                  <InfoDisplayValue value={ssoProvider.issuer} label="Issuer" />
                  {ssoProvider.discoveryUrl && (
                    <InfoDisplayValue value={ssoProvider.discoveryUrl} label="Discovery URL" />
                  )}
                  <InfoDisplayValue
                    value={ssoProvider.hasClientId ? "••••••••" : "Not configured"}
                    label="Client ID"
                  />
                  <InfoDisplayValue
                    value={ssoProvider.hasClientSecret ? "••••••••" : "Not configured"}
                    label="Client Secret"
                  />
                </>
              )}

              {/* Policy Details */}
              <InfoDisplayValue value={ssoProvider.enforced ? "Enabled" : "Disabled"} label="SSO Enforcement" />
              {ssoProvider.allowedDomains.length > 0 && (
                <InfoDisplayValue value={ssoProvider.allowedDomains.join(", ")} label="Allowed Domains" />
              )}
              <InfoDisplayValue
                value={ssoProvider.autoProvisionUsers ? "Enabled" : "Disabled"}
                label="Auto-provision Users"
              />
              {ssoProvider.defaultRole && <InfoDisplayValue value={ssoProvider.defaultRole} label="Default Role" />}

              {/* Metadata */}
              {ssoProvider.configuredByName && (
                <InfoDisplayValue value={ssoProvider.configuredByName} label="Configured By" />
              )}
            </VStack>
          </Flex>
        </CardBody>
      </Card>

      {/* Edit Modal */}
      <Modal size="5xl" isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Update SSO Configuration</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <SSOSettingsForm ssoProvider={ssoProvider} onClose={onClose} />
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelRef} onClose={onDeleteClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete SSO Configuration
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure? This will disable SSO authentication for all users. This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleDelete} ml={3} isLoading={deleteMutation.isPending}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
